import { useEffect, useRef, useState } from "react";
import { useI18n } from "../i18n";
import MarketDecisionGuide from "./MarketDecisionGuide";
import SignalTrackRecord from "./SignalTrackRecord";
import CoinNewsList from "./CoinNewsList";

const TABS = ["decision", "track", "news"];

export default function DecisionTrackTabs({
  selected,
  analyses,
  prices,
  marketContext,
  schedule,
  statusAvailable,
  onSelect,
  requestedTab,
}) {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState("decision");
  const [trackActivated, setTrackActivated] = useState(false);
  const [newsActivated, setNewsActivated] = useState(false);
  const tabRefs = useRef({});
  const activate = (tab) => {
    setActiveTab(tab);
    if (tab === "track") setTrackActivated(true);
    if (tab === "news") setNewsActivated(true);
  };
  useEffect(() => {
    if (TABS.includes(requestedTab)) activate(requestedTab);
  }, [requestedTab]);
  const moveTab = (event, direction) => {
    event.preventDefault();
    const index = TABS.indexOf(activeTab);
    const nextIndex = direction === "first" ? 0 : direction === "last" ? TABS.length - 1 : (index + direction + TABS.length) % TABS.length;
    const next = TABS[nextIndex];
    activate(next);
    tabRefs.current[next]?.focus();
  };
  const handleKeyDown = (event) => {
    if (event.key === "ArrowRight") moveTab(event, 1);
    else if (event.key === "ArrowLeft") moveTab(event, -1);
    else if (event.key === "Home") moveTab(event, "first");
    else if (event.key === "End") moveTab(event, "last");
    else if (event.key === "Enter" || event.key === " ") { event.preventDefault(); activate(event.currentTarget.dataset.tab); }
  };
  const decisionActive = activeTab === "decision";
  const trackActive = activeTab === "track";

  return (
    <section id="decision-track-tabs" className="border border-border/60 rounded-sm bg-[#09090B] overflow-hidden" data-testid="decision-track-tabs">
      <div role="tablist" aria-label={t("decision_track_tabs_label")} className="flex items-stretch border-b border-border/60 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        <Tab
          id="decision-track-tab-decision"
          panelId="decision-track-panel-decision"
          active={decisionActive}
          label={t("decision")}
          ariaLabel={t("market_decision_guide")}
          tab="decision"
          onClick={() => activate("decision")}
          onKeyDown={handleKeyDown}
          tabRef={(node) => { tabRefs.current.decision = node; }}
        />
        <Tab
          id="decision-track-tab-track"
          panelId="decision-track-panel-track"
          active={trackActive}
          label={t("history")}
          ariaLabel={t("track_title")}
          tab="track"
          onClick={() => activate("track")}
          onKeyDown={handleKeyDown}
          tabRef={(node) => { tabRefs.current.track = node; }}
        />
        <Tab
          id="decision-track-tab-news"
          panelId="coin-news-list"
          active={activeTab === "news"}
          label={<><span>{t("news")}</span><span className="decision-track-news-coin"> · {selected}</span></>}
          ariaLabel={`${t("news")} · ${selected}`}
          tab="news"
          onClick={() => activate("news")}
          onKeyDown={handleKeyDown}
          tabRef={(node) => { tabRefs.current.news = node; }}
        />
      </div>
      <div id="decision-track-panel-decision" role="tabpanel" aria-labelledby="decision-track-tab-decision" hidden={!decisionActive} tabIndex="0" className="focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400">
        <MarketDecisionGuide analyses={analyses} selected={selected} prices={prices} marketContext={marketContext} schedule={schedule} statusAvailable={statusAvailable} onSelect={onSelect} embedded />
      </div>
      <div id="decision-track-panel-track" role="tabpanel" aria-labelledby="decision-track-tab-track" hidden={!trackActive} tabIndex="0" className="focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400">
        {trackActivated && <SignalTrackRecord coin={selected} technicalCycleTimestamp={schedule?.technical?.last_completed_at} embedded />}
      </div>
      <div id="coin-news-list" role="tabpanel" aria-labelledby="decision-track-tab-news" hidden={activeTab !== "news"} tabIndex="0" className="focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400">
        {newsActivated && <CoinNewsList symbol={selected} />}
      </div>
    </section>
  );
}

function Tab({ id, panelId, active, label, ariaLabel, tab, onClick, onKeyDown, tabRef }) {
  return <button ref={tabRef} id={id} type="button" role="tab" data-tab={tab} aria-label={ariaLabel} title={ariaLabel} aria-selected={active} aria-controls={panelId} tabIndex={active ? 0 : -1} onClick={onClick} onKeyDown={onKeyDown} className={`min-w-0 flex-1 whitespace-nowrap overflow-hidden px-2 sm:px-3 py-3 text-[10px] sm:text-xs font-mono font-bold text-center transition-colors focus-visible:z-10 focus-visible:outline focus-visible:outline-1 focus-visible:outline-amber-400 ${active ? "text-amber-300 bg-amber-500/10" : "text-zinc-500 hover:text-zinc-200 hover:bg-white/[0.03]"}`}>{label}</button>;
}
